import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Cpu, 
  Rocket, 
  Award, 
  TrendingUp, 
  Briefcase,
  ArrowUpRight,
} from 'lucide-react';
import sap from "../Facilities/SAP.jpeg";

// --- BACKGROUND IMAGE OPTIONS (Uncomment the one you want to use) ---

// OPTION 1: Futuristic Global Network (Deep Blue/Purple) - Best for "Tech & Scale"
// const HERO_IMAGE = "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop";

// OPTION 2: Modern Abstract 3D Shapes (Clean/Minimal) - Best for "Design & Innovation"
// const HERO_IMAGE = "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2000&auto=format&fit=crop";

// OPTION 3: Startup Team/Collaboration (Warm/Human) - Best for "Community & Culture"
// const HERO_IMAGE = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop";
const HERO_IMAGE = sap;

// --- DATA ---
const successStories = [
  {
    id: 1,
    name: "Optimized Electrotech",
    sector: "Defence Tech",
    icon: <ShieldCheck className="w-5 h-5 text-white" />,
    gradient: "from-blue-500 to-blue-600",
    text: "text-blue-600",
    bg: "bg-blue-50",
    border: "group-hover:border-blue-200",
    description: "National leader in precision electro-optics and long-range surveillance systems for global defense.",
    metrics: [
      { label: "Total Funding", value: "₹50.37 Cr" },
      { label: "Key Investor", value: "Blume Ventures" }
    ],
    tag: "Series A Funded"
  },
  {
    id: 2,
    name: "Evigway Technologies",
    sector: "Aerospace",
    icon: <Rocket className="w-5 h-5 text-white" />,
    gradient: "from-indigo-500 to-indigo-600",
    text: "text-indigo-600",
    bg: "bg-indigo-50",
    border: "group-hover:border-indigo-200",
    description: "Pioneering unmanned logistics. Secured massive military contracts for tactical supply chain automation.",
    metrics: [
      { label: "Pipeline", value: "₹500 Cr+" },
      { label: "Govt Grants", value: "₹26.4 Cr" }
    ],
    tag: "iDEX Winner"
  },
  {
    id: 3,
    name: "RC Hobbytech",
    sector: "Robotics",
    icon: <TrendingUp className="w-5 h-5 text-white" />,
    gradient: "from-cyan-500 to-cyan-600",
    text: "text-cyan-600",
    bg: "bg-cyan-50",
    border: "group-hover:border-cyan-200",
    description: "AI-powered surveillance using unmanned systems. Scaling defense intelligence with institutional support.",
    metrics: [
      { label: "Valuation", value: "₹222 Cr" },
      { label: "Top Client", value: "India OPTEL" }
    ],
    tag: "Strategic Partner"
  },
  {
    id: 4,
    name: "Easecruit",
    sector: "HR Tech",
    icon: <Briefcase className="w-5 h-5 text-white" />,
    gradient: "from-purple-500 to-purple-600",
    text: "text-purple-600",
    bg: "bg-purple-50",
    border: "group-hover:border-purple-200",
    description: "Disrupting HR-Tech with deep-learning models for automated candidate screening and placement.",
    metrics: [
      { label: "Valuation", value: "₹31 Cr" },
      { label: "Funding", value: "₹3.5 Cr" }
    ],
    tag: "Next-Gen AI"
  },
  {
    id: 5,
    name: "Vanix Technologies",
    sector: "Consumer IoT",
    icon: <Cpu className="w-5 h-5 text-white" />,
    gradient: "from-emerald-500 to-emerald-600",
    text: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "group-hover:border-emerald-200",
    description: "Innovation in smart hardware and AI-integrated consumer interfaces for global markets.",
    metrics: [
      { label: "Funding", value: "₹1.06 Cr" },
      { label: "Recognition", value: "Startup India" }
    ],
    tag: "SISFS Winner"
  },
  {
    id: 6,
    name: "Jyoma Solutions",
    sector: "Defence",
    icon: <Award className="w-5 h-5 text-white" />,
    gradient: "from-orange-500 to-orange-600",
    text: "text-orange-600",
    bg: "bg-orange-50",
    border: "group-hover:border-orange-200",
    description: "Developing robust tactical hardware and surveillance software for modern combat requirements.",
    metrics: [
      { label: "Seed Fund", value: "₹1.24 Cr" },
      { label: "Award", value: "iDEX SPARK" }
    ],
    tag: "Defence Tech"
  }
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 50 } }
};

const SuccessStories = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      
      {/* --- CINEMATIC HERO SECTION --- */}
      <div className="relative pt-32 pb-32 lg:pt-48 lg:pb-40 px-6 overflow-hidden flex flex-col items-center justify-center">
        
        {/* 1. Background Image with Slow Zoom (Ken Burns Effect) */}
        <div className="absolute inset-0 z-0">
          <motion.img 
            initial={{ scale: 1 }}
            animate={{ scale: 1.1 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
            src={HERO_IMAGE}
            alt="Success Background" 
            className="w-full h-full object-cover"
          />
          {/* 2. Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-50"></div>
        </div>

        {/* 3. Hero Content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-widest mb-6 shadow-2xl"
          >
             <span>Portfolio Highlights</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight drop-shadow-sm"
          >
            Building the <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Future of Tech</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-slate-200 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-light"
          >
            {/* Discover the startups nurtured at IIT Ropar, now valued over <span className="font-semibold text-blue-1000">₹750 Crores</span> combined. */}
          </motion.p>
        </div>
      </div>

      {/* --- CARDS GRID (Overlapping the Hero) --- */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 -mt-20 pb-32">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
            {successStories.map((story) => (
                <SuccessCard key={story.id} story={story} />
            ))}
        </motion.div>
      </div>

    </div>
  );
};

// --- CARD COMPONENT ---
const SuccessCard = ({ story }) => (
  <motion.div
    variants={itemVariants}
    className={`group relative bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 hover:-translate-y-2 flex flex-col overflow-hidden h-full ${story.border}`}
  >
    {/* Card Header Section */}
    <div className="p-6 pb-0 flex justify-between items-start">
        {/* Icon with specific gradient background */}
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${story.gradient} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
            {story.icon}
        </div>
        
        {/* Sector Tag */}
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
            {story.sector}
        </span>
    </div>

    {/* Content Body */}
    <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors flex items-center gap-2">
            {story.name}
        </h3>
        
        {/* Achievement Tag */}
        <div className="mb-4">
             <span className={`inline-block text-[11px] font-bold px-2 py-1 rounded-md ${story.bg} ${story.text} bg-opacity-60`}>
                {story.tag}
            </span>
        </div>

        <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1">
            {story.description}
        </p>

        {/* Metrics Footer (Clean Split) */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            {story.metrics.map((m, i) => (
                <div key={i}>
                    <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">
                        {m.label}
                    </p>
                    <p className="text-base font-bold text-slate-900">
                        {m.value}
                    </p>
                </div>
            ))}
        </div>
    </div>
    
    {/* Hover Action: 'Learn More' arrow appears */}
    <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-x-2 group-hover:translate-x-0">
         <ArrowUpRight className="w-5 h-5 text-slate-300" />
    </div>
  </motion.div>
);

export default SuccessStories;